/**
 * Notification Store Slice
 *
 * Manages notification state including real-time updates.
 */

import type { StateCreator } from 'zustand';
import type { NotificationPayload } from '@/lib/websocket/events';

// =============================================================================
// TYPES
// =============================================================================

export interface Notification extends NotificationPayload {
  read: boolean;
}

export interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  isOpen: boolean; // For notification panel/dropdown
}

export interface NotificationActions {
  // State setters
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: NotificationPayload) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  setUnreadCount: (count: number) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setOpen: (isOpen: boolean) => void;
  toggleOpen: () => void;
}

export type NotificationSlice = NotificationState & NotificationActions;

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  isOpen: false,
};

// =============================================================================
// SLICE CREATOR
// =============================================================================

export const createNotificationSlice: StateCreator<
  NotificationSlice,
  [],
  [],
  NotificationSlice
> = (set) => ({
  ...initialState,

  setNotifications: (notifications) => {
    const unreadCount = notifications.filter((n) => !n.read).length;
    set({ notifications, unreadCount });
  },

  addNotification: (notification) => {
    const newNotification: Notification = {
      ...notification,
      read: notification.read ?? false,
    };

    set((state) => ({
      notifications: [newNotification, ...state.notifications],
      unreadCount: newNotification.read ? state.unreadCount : state.unreadCount + 1,
    }));
  },

  markAsRead: (id) => {
    set((state) => {
      const notification = state.notifications.find((n) => n.id === id);
      if (!notification || notification.read) {
        return state;
      }

      return {
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    });
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  },

  removeNotification: (id) => {
    set((state) => {
      const notification = state.notifications.find((n) => n.id === id);
      const wasUnread = notification && !notification.read;

      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    });
  },

  clearAll: () => {
    set({ notifications: [], unreadCount: 0 });
  },

  setUnreadCount: (count) => {
    set({ unreadCount: count });
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },

  setError: (error) => {
    set({ error });
  },

  setOpen: (isOpen) => {
    set({ isOpen });
  },

  toggleOpen: () => {
    set((state) => ({ isOpen: !state.isOpen }));
  },
});
